// src/lib/thermo/pseudocomponents/loaders.js
// Normalizes your pseudo-component sets into a single, SI-consistent shape.

import pseudo_WCS from "./pseudoComponents_WCS";
import pseudo_ArabLight from "./pseudoComponents_ArabLight";
import pseudo_Brent from "./pseudoComponents_Brent";
import pseudo_VenezHeavy from "./pseudoComponents_VenezHeavy";
import pseudo_WTI from "./pseudoComponents_WTI";
import { crudeCompositions } from "./componentData"; // optional: for feed z defaults

// ---- Unit helpers ----
const MPa_to_Pa = (x) => x * 1e6;
const bar_to_Pa = (x) => x * 1e5;
const C_to_K = (x) => x + 273.15;

// Some of your sources mix Pc units. Based on magnitudes:
// - WCS Pc values ~12–16.5, ~look like *bar* for heavy ends in that file (comment even says Tc/Pc≈~56 in K/bar).
// - Other crudes Pc ~2–10 -> *MPa*.
// We encode that here explicitly so everything becomes Pa downstream.
const sourceMeta = {
  "Western Canadian Select": { PcUnit: "bar", TbUnit: "C" },
  "Arab Light": { PcUnit: "MPa", TbUnit: "C" },
  Brent: { PcUnit: "MPa", TbUnit: "C" },
  "Venezuelan Heavy": { PcUnit: "MPa", TbUnit: "C" },
  "West Texas Intermediate": { PcUnit: "MPa", TbUnit: "C" },
};

// Volume shift placeholder (per-component). In PR/PRSV+vol-shift you add a shift to Z via b.
// Here we provide a simple correlation you can revise once we tune data.
function estimateVolumeShift(omega, SG, MW) {
  // Very rough: more heavy & higher ω -> larger shift. Returns a dimensionless factor
  // typically applied as V_shift = δ * b. Start small.
  const w = Math.max(-0.1, Math.min(omega ?? 0, 1));
  const heaviness = Math.max(0, (SG ?? 0.7) - 0.6); // 0..~0.3
  const size = Math.max(0, (MW ?? 200) - 100) / 400; // 0..~1
  return 0.05 + 0.25 * w + 0.2 * heaviness + 0.1 * size; // ~0.05–0.4
}

// Build a coarse kij block matrix by TBP cuts (light↔heavy interaction > light↔light).
// You can replace with a full binary matrix later.
function buildKijMatrix(components) {
  const n = components.length;
  const kij = Array.from({ length: n }, () => Array(n).fill(0));

  // Define 5 TBP blocks by index percentiles
  const idx = (p) => Math.min(n - 1, Math.max(0, Math.floor(p * (n - 1))));
  const blocks = [
    [0, idx(0.1)], // C1–C4-ish
    [idx(0.1) + 1, idx(0.25)], // C5–C7
    [idx(0.25) + 1, idx(0.45)], // naphtha
    [idx(0.45) + 1, idx(0.7)], // kerosene–LGO
    [idx(0.7) + 1, n - 1], // HGO–Resid
  ];

  const val = (a, b) => {
    if (a === b) return 0.0;
    const d = Math.abs(a - b);
    if (d === 1) return 0.015; // neighboring blocks
    if (d === 2) return 0.02;
    if (d >= 3) return 0.03; // far-apart (light↔heavy)
    return 0.0;
  };

  for (let A = 0; A < blocks.length; A++) {
    for (let B = A; B < blocks.length; B++) {
      const kijAB = val(A, B);
      for (let i = blocks[A][0]; i <= blocks[A][1]; i++) {
        for (let j = blocks[B][0]; j <= blocks[B][1]; j++) {
          kij[i][j] = kij[j][i] = kijAB;
        }
      }
    }
  }
  return kij;
}

// Normalize one set (convert units, sort by Tb, attach delta & kij later)
function normalizeSet(raw, meta, crudeName) {
  const { PcUnit, TbUnit } = meta;
  const toPa =
    PcUnit === "MPa" ? MPa_to_Pa : PcUnit === "bar" ? bar_to_Pa : (x) => x;
  const TbConv = TbUnit === "C" ? C_to_K : (x) => x;

  // Clean + convert
  let pcs = raw.map((c, i) => {
    const TcK = c.Tc; // files already use K for Tc in most sets
    const PcPa = toPa(c.Pc);
    const TbK = TbConv(c.Tb);
    const MW = c.MW;
    const omega = c.omega;
    const SG = c.SG;

    return {
      name: c.name ?? `PC${String(i + 1).padStart(2, "0")}`,
      Tb: TbK,
      Tc: TcK,
      Pc: PcPa,
      omega,
      SG,
      MW,
      // EOS extras
      delta: estimateVolumeShift(omega, SG, MW),
    };
  });

  // Sort by TBP (ascending)
  pcs.sort((a, b) => a.Tb - b.Tb);

  // Basic sanity clamps (avoid pathological EOS inputs)
  pcs = pcs.map((c) => ({
    ...c,
    Tc: Math.max(200, Math.min(1200, c.Tc)),
    Pc: Math.max(1e5, Math.min(2e8, c.Pc)),
    omega: Math.max(-0.15, Math.min(1.2, c.omega ?? 0)),
    MW: Math.max(12, Math.min(1500, c.MW ?? 200)),
  }));

  // Attach kij blocks
  const kij = buildKijMatrix(pcs);

  // Browser-safe dev log (no 'process' dependency)
  if (typeof window !== "undefined") {
    const pcMin = Math.min(...pcs.map((c) => c.Pc));
    const pcMax = Math.max(...pcs.map((c) => c.Pc));
    if (pcMax < 2e6) {
      console.warn(
        `[${crudeName}] Pc range looks low (${(pcMin / 1e5).toFixed(1)}–${(
          pcMax / 1e5
        ).toFixed(1)} bar). Check units metadata.`
      );
    }
  }

  return { components: pcs, kij };
}

// Registry of your raw sets
const RAW = {
  "Western Canadian Select": pseudo_WCS,
  Brent: pseudo_Brent,
  "West Texas Intermediate": pseudo_WTI,
  "Arab Light": pseudo_ArabLight,
  "Venezuelan Heavy": pseudo_VenezHeavy,
};

// Main entry: get normalized components + kij (and optional default feed z)
export function getCrudeSet(name) {
  const raw = RAW[name];
  if (!raw) throw new Error(`Unknown crude: ${name}`);
  const meta = sourceMeta[name] || { PcUnit: "MPa", TbUnit: "C" };
  const { components, kij } = normalizeSet(raw, meta, name);

  // Optional: a default feed composition vector (same length as components)
  let zDefault = undefined;
  try {
    const z30 = crudeCompositions[name];
    if (Array.isArray(z30) && z30.length === components.length) {
      zDefault = z30.slice();
    }
  } catch (e) {
    // ignore
  }

  return { name, components, kij, zDefault };
}

// Convenience: list available crudes
export function listCrudes() {
  return Object.keys(RAW);
}
